import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, X } from "lucide-react";
import { generateStylePreview } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StylePreviewDialog — generates + shows a sample image for a style
 * preset (optionally themed to the channel's topic) so users can see
 * a style before committing. Self-manages fetch/cache/regenerate.
 */
export default function StylePreviewDialog({
  open, onClose, presetKey, presetLabel, channelHandle = "",
}) {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState("");
  const [cached, setCached] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (force) => {
    setLoading(true);
    setError("");
    if (force) setUrl("");
    try {
      const res = await generateStylePreview(presetKey, { channelHandle, force });
      setUrl(res.url || "");
      setCached(!!res.cached);
    } catch (err) {
      setError(err?.response?.data?.detail || "Preview generation failed — try again.");
    } finally {
      setLoading(false);
    }
  }, [presetKey, channelHandle]);

  useEffect(() => {
    if (open && presetKey) {
      setUrl("");
      setCached(false);
      load(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetKey]);

  if (!open) return null;

  return (
    <div
      data-testid="style-preview-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-sm max-w-2xl w-full p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400">
              // STYLE PREVIEW
            </div>
            <div className="font-display text-lg font-bold text-white leading-snug">
              {presetLabel || presetKey}
            </div>
            {channelHandle ? (
              <div className="text-xs text-zinc-400 mt-0.5">
                Sample themed to @{String(channelHandle).replace(/^@/, "")}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            data-testid="style-preview-close-btn"
            onClick={onClose}
            className="text-zinc-500 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-black aspect-video flex items-center justify-center overflow-hidden">
          {loading ? (
            <div
              data-testid="style-preview-loading"
              className="flex flex-col items-center gap-3 text-zinc-400 p-6 text-center"
            >
              <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
              <div className="font-mono text-xs">
                Generating a sample image… this takes 30–60 seconds.
              </div>
            </div>
          ) : error ? (
            <div
              data-testid="style-preview-error"
              className="text-red-400 font-mono text-xs p-6 text-center"
            >
              {error}
            </div>
          ) : url ? (
            <img
              data-testid="style-preview-image"
              src={`${BACKEND_URL}${url}`}
              alt={`${presetLabel || presetKey} style sample`}
              className="w-full h-full object-contain"
            />
          ) : null}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="style-preview-regenerate-btn"
            onClick={() => load(true)}
            disabled={loading}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] border border-yellow-400/60 text-yellow-400 hover:bg-yellow-400/10 px-3 py-1.5 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={loading ? "w-3.5 h-3.5 animate-spin" : "w-3.5 h-3.5"} />
            Regenerate
          </button>
          {cached && !loading && (
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              cached sample
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
